/** @jsxImportSource @emotion/react */

import { FC, useCallback, useMemo, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { FormApi } from 'final-form'
import { useMutation } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@mui/material'
import { OnChange } from 'react-final-form-listeners'
import { toast } from 'utils/toast'
import { findAllQuestionCategory_findAllQuestionCategory_items as QuestionCategory } from '__generatedTypes__/findAllQuestionCategory'
import { createQuestionCategory, createQuestionCategoryVariables } from '__generatedTypes__/createQuestionCategory'
import { removeQuestionCategory, removeQuestionCategoryVariables } from '__generatedTypes__/removeQuestionCategory'
import { updateQuestionCategory, updateQuestionCategoryVariables } from '__generatedTypes__/updateQuestionCategory'
import { CREATE_QUESTION_CATEGORY, REMOVE_QUESTION_CATEGORY, UPDATE_QUESTION_CATEGORY } from 'queries/questionCategory'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { MultiSelectField } from 'components/MultiSelect'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { PlatformSlug } from 'constants/consts'
import { Platform, SubjectGroup } from 'pagesNew/Questions/Form/useQuestionHelpers'
import { validateRequired } from 'utils/validation'
import { CannotDeleteCategoryModal } from '../CannotDeleteCategoryModal/CannotDeleteCategoryModal'
import * as classes from './styles'

interface IProps {
  questionCategory?: QuestionCategory | null
  allSubjectGroups: SubjectGroup[]
  allPlatforms: Platform[]
  getAvailableSubjectGroups: (selectedPlatforms?: PlatformSlug[]) => SubjectGroup[]
  currentPlatformSlug?: PlatformSlug
  handleClose: () => void
}

interface FormValues {
  title: string
  platforms: Platform[]
  subjectGroup: SubjectGroup | null
}

const QuestionCategoryModal: FC<IProps> = ({
  questionCategory,
  allSubjectGroups,
  allPlatforms,
  getAvailableSubjectGroups,
  currentPlatformSlug,
  handleClose,
}) => {
  const [showUsageModal, setShowUsageModal] = useState<boolean>(false)

  const [createQuestionCategory, { loading: loadingCreate }] = useMutation<
    createQuestionCategory,
    createQuestionCategoryVariables
  >(CREATE_QUESTION_CATEGORY)

  const [removeQuestionCategory, { loading: loadingRemove }] = useMutation<
    removeQuestionCategory,
    removeQuestionCategoryVariables
  >(REMOVE_QUESTION_CATEGORY)

  const [updateQuestionCategory, { loading: loadingUpdate }] = useMutation<
    updateQuestionCategory,
    updateQuestionCategoryVariables
  >(UPDATE_QUESTION_CATEGORY)

  const loading = loadingCreate || loadingRemove || loadingUpdate

  const initialValues: FormValues = useMemo(() => {
    const subjectGroup = questionCategory?.subjects?.length
      ? allSubjectGroups.find((sg) =>
          questionCategory.subjects?.every((subject) => sg.subjects.some((s) => s.id === subject.id))
        ) ?? null
      : null

    let platforms: Platform[] = []

    if (questionCategory?.subjects?.length) {
      const platformIds = Array.from(new Set(questionCategory.subjects.map((s) => s.platform.id)))
      platforms = platformIds.map((id) => allPlatforms.find((p) => p.id === id)).filter((p): p is Platform => p != null)
    } else if (currentPlatformSlug) {
      const match = allPlatforms.find((p) => p.slug === currentPlatformSlug)
      if (match) {
        platforms = [match]
      }
    }

    return {
      title: questionCategory?.title ?? '',
      platforms,
      subjectGroup,
    }
  }, [questionCategory, allSubjectGroups, allPlatforms, currentPlatformSlug])

  const handlePlatformsChange = useCallback(
    (platforms: Platform[], form: FormApi<FormValues, Partial<FormValues>>) => {
      const slugs = platforms?.length ? platforms.map((p) => p.slug as PlatformSlug) : undefined
      const newSubjectGroups = getAvailableSubjectGroups(slugs)

      if (newSubjectGroups.length === 1) {
        form.change('subjectGroup', newSubjectGroups[0])
        return
      }

      const selectedSubjectGroupId = form.getFieldState('subjectGroup')?.value?.id
      const nextSubjectGroup = selectedSubjectGroupId
        ? newSubjectGroups.find((g) => g.id === selectedSubjectGroupId)
        : undefined
      form.change('subjectGroup', nextSubjectGroup ?? null)
    },
    [getAvailableSubjectGroups]
  )

  const handleSubmit = async (values: FormValues) => {
    const subjects = values.subjectGroup?.subjects.map((subject) => ({ id: subject.id })) ?? []

    try {
      if (questionCategory) {
        await updateQuestionCategory({
          variables: {
            params: {
              id: questionCategory.id,
              title: values.title,
              subjects,
            },
          },
        })
        toast.success('Question category updated successfully')
      } else {
        await createQuestionCategory({
          variables: {
            params: {
              title: values.title,
              subjects,
            },
          },
        })
        toast.success('Question category created successfully')
      }
      handleClose()
    } catch (e) {
      toast.error('Failed to create/update question category')
    }
  }

  const onDeleteClick = async () => {
    if (!questionCategory) {
      return
    }

    try {
      await removeQuestionCategory({ variables: { removeQuestionCategoryId: questionCategory.id } })
      toast.success('Question category deleted successfully')
      handleClose()
    } catch (e) {
      setShowUsageModal(true)
    }
  }

  return (
    <FinalForm<FormValues>
      key={questionCategory?.id ?? 'create'}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => {
        const platformSlugs = values.platforms?.length ? values.platforms.map((p) => p.slug as PlatformSlug) : undefined
        const filteredSubjectGroups = getAvailableSubjectGroups(platformSlugs)

        return (
          <form onSubmit={handleSubmit}>
            <OnChange name="platforms">{(platforms) => handlePlatformsChange(platforms ?? [], form)}</OnChange>
            <div css={classes.container}>
              <Typography css={classes.title} variant="title2">
                {questionCategory ? 'Edit Question Category' : 'Create New Question Category'}
              </Typography>
              <InputField label="Title" name="title" fullWidth placeholder="Enter Title" validate={validateRequired} />
              <MultiSelectField<Platform>
                name="platforms"
                validate={validateRequired}
                items={allPlatforms}
                renderItem={(platform) => getFullPlatformTitle(platform.title)}
                getItemLabel={(platform) => getFullPlatformTitle(platform.title) ?? platform.title}
                label="Platforms"
                placeholder="Choose Platforms"
                displaySelectedItemsFirst
                changeImmediately
                fullWidth
              />
              <SelectField<SubjectGroup>
                name="subjectGroup"
                items={filteredSubjectGroups}
                label="Subject"
                placeholder="Choose Subject"
                displaySelectedItemsFirst
                fullWidth
                validate={validateRequired}
              />
              {!!questionCategory && (
                <CannotDeleteCategoryModal
                  open={showUsageModal}
                  questionCategory={questionCategory}
                  onClose={() => setShowUsageModal(false)}
                />
              )}
              <Button fullWidth size="large" color="blue" type="submit" disabled={loading}>
                {loadingCreate || loadingUpdate ? (
                  <CircularProgress size={16} color="inherit" />
                ) : questionCategory ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
              {!!questionCategory && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="red"
                  onClick={onDeleteClick}
                  disabled={loading}
                >
                  {loadingRemove ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <TrashIcon />
                      Delete
                    </>
                  )}
                </Button>
              )}
              <Typography variant="body3" css={classes.hint}>
                To cancel click on a background
              </Typography>
            </div>
          </form>
        )
      }}
    />
  )
}

export default QuestionCategoryModal
